/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faBell,
  faEnvelope,
  faBars,
  faUser,
  faSignOutAlt,
  faUniversity,
  faChartLine,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import './UniversityDashboardHeader.css';

const UniversityDashboardHeader = ({ 
  currentPage = 'Dashboard', 
  onSearch, 
  onMenuToggle
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState(null);
  
  const notificationRef = useRef(null);
  const messageRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    fetchNotificationCount();
    fetchMessageCount();
    fetchProfilePhoto();
    const interval = setInterval(() => {
      fetchNotificationCount();
      fetchMessageCount();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchProfilePhoto = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(buildApiUrl('/api/user/profile'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      
      if (response.data) {
        const photoPath = response.data.profilePhotoPath || response.data.profileImage || response.data.profilePhoto;
        if (photoPath) {
          let normalizedPath = photoPath.replace(/\\/g, '/');
          let fullUrl = '';
          if (normalizedPath.startsWith('uploads/')) {
            const uploadPath = normalizedPath.replace('uploads/', '');
            fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
          } else if (normalizedPath.startsWith('/uploads/')) {
            const uploadPath = normalizedPath.replace('/uploads/', '');
            fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
          } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            fullUrl = normalizedPath;
          } else {
            fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
          }
          setProfilePhoto(fullUrl);
        }
      }
    } catch (error) {
      console.log('Error fetching profile photo:', error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        setShowMessages(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setNotificationCount(0);
        setNotifications([]);
        return;
      }
      
      const notifResponse = await axios.get(buildApiUrl('/api/notifications'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      
      if (notifResponse.data && notifResponse.data.success) {
        const notifs = notifResponse.data.notifications || [];
        setNotifications(notifs);
        const unreadCount = notifs.filter(n => !n.read).length;
        setNotificationCount(unreadCount);
      } else {
        try {
          const response = await axios.get(buildApiUrl('/api/notifications/unread-count'), {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          if (response.data && response.data.success) {
            setNotificationCount(response.data.unread_count || 0);
          }
        } catch (fallbackError) {
          console.log('Fallback notification count fetch failed:', fallbackError);
          setNotificationCount(0);
        }
      }
    } catch (error) {
      if (error.code !== 'ECONNABORTED' && error.response?.status !== 404) {
        console.log('Error fetching notifications:', error);
      }
      setNotificationCount(0);
    }
  };

  const fetchMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setMessageCount(0);
        setMessages([]);
        return;
      }
      
      const messageResponse = await axios.get(buildApiUrl('/api/messages'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      
      if (messageResponse.data && messageResponse.data.success) {
        const msgs = messageResponse.data.messages || [];
        setMessages(msgs);
        const unreadCount = msgs.filter(m => !m.read).length;
        setMessageCount(unreadCount);
      } else {
        try {
          const response = await axios.get(buildApiUrl('/api/messages/unread-count'), {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          if (response.data && response.data.success) {
            setMessageCount(response.data.unread_count || 0);
          }
        } catch (fallbackError) {
          console.log('Fallback message count fetch failed:', fallbackError);
          setMessageCount(0);
        }
      }
    } catch (error) {
      if (error.code !== 'ECONNABORTED' && error.response?.status !== 404) {
        console.log('Error fetching messages:', error);
      }
      setMessageCount(0);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchTerm);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getUserName = () => {
    if (!user) return 'User';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'User';
  };

  const getUserRole = () => {
    return 'University Administrator';
  };

  return (
    <header className="university-dashboard-header">
      <div className="university-header-left">
        {/* Logo */}
        <div className="university-header-logo">
          <div className="university-logo-icon">
            <FontAwesomeIcon icon={faUniversity} />
          </div>
          <div className="university-logo-text">
            <div className="university-logo-name">UNIVERSITY HUB</div>
            <div className="university-logo-subtitle">Akshar Jobs</div>
          </div>
        </div>
      </div>

      <div className="university-header-right">
        {/* Notifications */}
        <div className="university-header-icon-wrapper" ref={notificationRef}>
          <button 
            className="university-header-icon-btn"
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowMessages(false);
              setShowUserMenu(false);
            }}
          >
            <FontAwesomeIcon icon={faBell} />
            {notificationCount > 0 && (
              <span className="university-icon-badge">{notificationCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="university-dropdown-menu university-notifications-dropdown">
              <div className="university-dropdown-header">
                <h4>Notifications</h4>
                {notificationCount > 0 && (
                  <button 
                    className="university-mark-all-read"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(
                          buildApiUrl('/api/notifications/mark-all-read'),
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        fetchNotificationCount();
                      } catch (error) {
                        console.log('Error marking all as read:', error);
                      }
                    }}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="university-dropdown-content">
                {notifications.length > 0 ? (
                  notifications.slice(0, 10).map((notification, index) => (
                    <div 
                      key={notification._id || index} 
                      className={`university-notification-item ${!notification.read ? 'unread' : ''}`}
                      onClick={async () => {
                        if (!notification.read) {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.put(
                              buildApiUrl(`/api/notifications/${notification._id}/read`),
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            fetchNotificationCount();
                          } catch (error) {
                            console.log('Error marking as read:', error);
                          }
                        }
                      }}
                    >
                      <p>{notification.message || notification.title || 'New notification'}</p>
                      <span>
                        {notification.created_at ? new Date(notification.created_at).toLocaleString() : 'Just now'}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="university-empty-dropdown">
                    <FontAwesomeIcon icon={faBell} />
                    <p>No new notifications</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="university-header-icon-wrapper" ref={messageRef}>
          <button 
            className="university-header-icon-btn"
            onClick={() => {
              setShowMessages(!showMessages);
              setShowNotifications(false);
              setShowUserMenu(false);
            }}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            {messageCount > 0 && (
              <span className="university-icon-badge">{messageCount}</span>
            )}
          </button>

          {showMessages && (
            <div className="university-dropdown-menu university-messages-dropdown">
              <div className="university-dropdown-header">
                <h4>Messages</h4>
                {messageCount > 0 && (
                  <button 
                    className="university-mark-all-read"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(
                          buildApiUrl('/api/messages/mark-all-read'),
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        fetchMessageCount();
                      } catch (error) {
                        console.log('Error marking all messages as read:', error);
                      }
                    }}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              <div className="university-dropdown-content">
                {messages.length > 0 ? (
                  messages.slice(0, 10).map((message, index) => (
                    <div 
                      key={message._id || index} 
                      className={`university-message-item ${!message.read ? 'unread' : ''}`}
                      onClick={() => navigate('/messages')}
                    >
                      <p>{message.sender_name || message.from_name || 'Unknown Sender'}</p>
                      <p>{message.message || message.content || 'New message'}</p>
                    </div>
                  ))
                ) : (
                  <div className="university-empty-dropdown">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <p>No new messages</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Profile Menu */}
        <div className="university-header-user-wrapper" ref={userMenuRef}>
          <div 
            className="university-header-user-info"
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
              setShowMessages(false);
            }}
          >
            <div className="university-user-details">
              <div className="university-user-name">{getUserName()}</div>
              <div className="university-user-role">{getUserRole()}</div>
            </div>
            <div className="university-user-avatar">
              {profilePhoto ? (
                <img 
                  src={profilePhoto}
                  alt={getUserName()}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                  onError={() => setProfilePhoto(null)}
                />
              ) : (
                <div className="university-avatar-initials">{getUserInitials()}</div>
              )}
            </div>
          </div>

          {showUserMenu && (
            <div className="university-dropdown-menu university-user-dropdown">
              <div className="university-user-dropdown-header">
                <div>
                  <div className="university-dropdown-user-name">{getUserName()}</div>
                  <div className="university-dropdown-user-email">{user?.email}</div>
                </div>
                <div className="university-user-avatar-large">
                  {profilePhoto ? (
                    <img 
                      src={profilePhoto}
                      alt={getUserName()}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={() => setProfilePhoto(null)}
                    />
                  ) : (
                    <div className="university-avatar-initials">{getUserInitials()}</div>
                  )}
                </div>
              </div>
              <div className="university-dropdown-divider"></div>
              <div className="university-dropdown-content">
                <div className="university-dropdown-item" onClick={() => { navigate('/university-dashboard'); setShowUserMenu(false); }}>
                  <FontAwesomeIcon icon={faChartLine} />
                  <span>Dashboard</span>
                </div>
                <div className="university-dropdown-item" onClick={() => { navigate('/settings'); setShowUserMenu(false); }}>
                  <FontAwesomeIcon icon={faCog} />
                  <span>Settings</span>
                </div>
                <div className="university-dropdown-divider"></div>
                <div className="university-dropdown-item university-logout-item" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Logout</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default UniversityDashboardHeader;

